import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useSessao } from '../../auth/contexto'
import { caminhoRelativo, iniciarLogin } from '../../auth/login'
import { consultarModosDeEntrada, type ModosDeEntrada } from '../../auth/loginLocal'
import { Botao } from '../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { IconeAlerta } from '../../components/Icones'
import { cn } from '../../lib/cn'
import { TEXTOS } from '../../lib/glossario'
import { versaoDoSistema } from '../../lib/versao'
import { FormularioDeEntrada } from './FormularioDeEntrada'

/**
 * As formas de entrar não pertencem a nenhum recurso de negócio e são a única consulta feita
 * antes de existir sessão — daí a chave viver aqui, e não no registro de chaves de GMUD.
 */
const CHAVE_DOS_MODOS = ['auth', 'modos'] as const

/**
 * Entrada no sistema.
 *
 * **Quem decide como se entra é o servidor.** `GET /auth/modos` diz se este ambiente tem login
 * institucional, login por usuário e senha, os dois ou nenhum, e a tela desenha o que vier.
 * É o mesmo princípio de `/gmuds/{id}/transicoes-possiveis`: chumbar "sem Entra ID por
 * enquanto" deixaria o botão escondido no dia em que o tenant for configurado, e a descoberta
 * viria por chamado.
 *
 * Três avisos diferentes, de propósito, porque as saídas são diferentes:
 * 1. recusa do provedor ("a tentativa expirou", "seu acesso está suspenso") chega do backend no
 *    fragmento da URL e se resolve tentando de novo ou falando com a SSTD;
 * 2. nenhuma forma de entrar configurada não é erro de quem usa — é configuração do servidor, e
 *    dizer "falha ao entrar" faz servidor abrir chamado do que não é problema dele;
 * 3. credencial recusada fica dentro do formulário, junto dos campos, porque é ali que se age.
 *
 * @returns a tela de entrada
 */
export function PaginaLogin() {
  const { autenticado, erroDeLogin, descartarErroDeLogin } = useSessao()
  const local = useLocation()
  const navegar = useNavigate()
  const [aviso, setAviso] = useState<string | null>(null)
  const [abrindoEntra, setAbrindoEntra] = useState(false)
  const modos = useQuery({
    queryKey: CHAVE_DOS_MODOS,
    queryFn: consultarModosDeEntrada,
    retry: false,
    staleTime: Infinity,
  })

  const destino = caminhoRelativo((local.state as { destino?: string } | null)?.destino ?? '/painel')

  if (autenticado) {
    return <Navigate to={destino} replace />
  }

  function limparAvisos() {
    setAviso(null)
    descartarErroDeLogin()
  }

  async function entrarPeloEntra() {
    setAbrindoEntra(true)
    limparAvisos()
    const problema = await iniciarLogin(destino)
    if (problema) {
      setAviso(problema)
      setAbrindoEntra(false)
    }
  }

  const mensagem = aviso ?? erroDeLogin

  return (
    <main className="grid min-h-screen lg:grid-cols-2">
      <PainelInstitucional />

      <section className="flex flex-col justify-center px-6 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-sm">
          <div className="lg:hidden">
            <img src="/img/brasao-niteroi.svg" alt="" className="h-12 w-auto" />
            <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-niteroi-700">
              SEXEC · SSTD · Niterói
            </p>
          </div>

          <h1 className="titulo-da-pagina mt-4 lg:mt-0">Entrar na {TEXTOS.nomeDoSistema}</h1>

          {mensagem && (
            <p role="alert" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              {mensagem}
            </p>
          )}

          <div className="cartao mt-5 p-5 sm:p-6">
            {modos.isPending && <Esqueleto linhas={4} />}

            {modos.isError && (
              <div className="space-y-4">
                <AvisoDeErro erro={modos.error} titulo="Não foi possível saber como entrar neste ambiente" />
                <Botao className="w-full" onClick={() => void modos.refetch()}>
                  Tentar de novo
                </Botao>
              </div>
            )}

            {modos.data && (
              <FormasDeEntrar
                modos={modos.data}
                abrindoEntra={abrindoEntra}
                aoTentar={limparAvisos}
                aoEntrar={() => navegar(destino, { replace: true })}
                aoEntrarPeloEntra={entrarPeloEntra}
              />
            )}
          </div>

          <p className="mt-4 text-xs text-slate-500">
            Problemas para entrar? Fale com a equipe da SSTD. Não há cadastro separado neste sistema.
          </p>
        </div>
      </section>
    </main>
  )
}

/**
 * O que o servidor oferece, e só.
 *
 * Sem `else`: os dois modos podem estar ligados ao mesmo tempo (é o que acontece enquanto o
 * tenant é configurado e o acesso por senha ainda atende a contratada), e nenhum dos dois é
 * um estado possível — em vez de uma tela vazia, ele ganha a explicação de que a falta é de
 * configuração do servidor.
 *
 * @param props os modos, o estado do botão institucional e o que fazer em cada caminho
 * @returns as formas de entrar disponíveis
 */
function FormasDeEntrar({
  modos,
  abrindoEntra,
  aoTentar,
  aoEntrar,
  aoEntrarPeloEntra,
}: {
  modos: ModosDeEntrada
  abrindoEntra: boolean
  aoTentar: () => void
  aoEntrar: () => void
  aoEntrarPeloEntra: () => void
}) {
  if (!modos.usuarioESenha && !modos.entraId) {
    return (
      <p className="flex items-start gap-2 text-sm text-slate-700">
        <IconeAlerta className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <span>{TEXTOS.loginNaoConfigurado}</span>
      </p>
    )
  }

  return (
    <div className="space-y-4">
      {modos.usuarioESenha && <FormularioDeEntrada aoTentar={aoTentar} aoEntrar={aoEntrar} />}

      {modos.usuarioESenha && modos.entraId && (
        <p className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400">
          <span aria-hidden="true" className="h-px flex-1 bg-slate-200" />
          ou
          <span aria-hidden="true" className="h-px flex-1 bg-slate-200" />
        </p>
      )}

      {modos.entraId && (
        <Botao
          aparencia={modos.usuarioESenha ? 'secundario' : 'primario'}
          className="w-full"
          onClick={aoEntrarPeloEntra}
          disabled={abrindoEntra}
        >
          {abrindoEntra ? 'Abrindo o login…' : TEXTOS.entrar}
        </Botao>
      )}
    </div>
  )
}

/**
 * A metade institucional, que some no celular.
 *
 * O nome do sistema aqui é um parágrafo e não um segundo `<h1>` — o título da página é
 * "Entrar na Fiscalização", na coluna do formulário, e dois começos de nível 1 obrigam quem
 * navega por cabeçalhos a escolher por onde entrar na tela. Mesma escolha da `BarraSuperior`.
 *
 * O brasão vai com `alt` vazio: ele repete o que a linha ao lado já diz por escrito, e um
 * leitor de tela anunciando "Brasão de Niterói, SEXEC · SSTD · Niterói" lê a mesma informação
 * duas vezes. É o arquivo que já está em `public/img`, servido da mesma origem — nada de
 * imagem de fundo pesada nem de CDN, que a CSP bloqueia.
 *
 * @returns o painel da esquerda
 */
function PainelInstitucional() {
  return (
    <aside
      className={cn(
        'relative hidden flex-col items-center justify-center overflow-hidden p-10 text-center text-white lg:flex',
        'bg-gradient-to-b from-niteroi-700 to-niteroi-900',
      )}
    >
      {/* Um halo atrás do brasão, para o painel não ser um retângulo azul chapado. Fica no
          gradiente, sem imagem: a CSP é estrita e um PNG de fundo custaria mais que o ganho. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 h-[28rem] w-[28rem] rounded-full bg-niteroi-500/25 blur-3xl"
      />

      {/* Brasão e nome são UM bloco, centralizado — e não dois itens que o `justify-between`
          empurra para cantos opostos, que foi como o brasão acabou órfão no alto da tela.
          `w-auto` sozinho não segura a proporção aqui: num container `flex-col` o `align-items`
          padrão é `stretch` e o item estica na largura, deformando um SVG que é retrato (500×620).
          Daí o `shrink-0`, e a largura vir da altura. */}
      <div className="relative flex flex-col items-center">
        <img src="/img/brasao-niteroi.svg" alt="" className="h-44 w-auto shrink-0 drop-shadow-lg" />

        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-niteroi-200">
          SEXEC · SSTD · Niterói
        </p>
        <p className="mt-3 text-4xl font-bold tracking-tight">{TEXTOS.nomeDoSistema}</p>
        {/* Acento em preenchimento, nunca em texto: #f85808 sobre escuro não passa em AA como letra. */}
        <div aria-hidden="true" className="mt-6 h-1 w-16 rounded-full bg-laranja-500" />
      </div>

      {/* Mesma identificação do rodapé das telas logadas, e a mesma versão: quem abre um chamado
          dizendo "não consigo entrar" é atendido por alguém que precisa saber qual build a pessoa
          está vendo — e a tela de entrada é a única que ela alcança. */}
      <div className="absolute bottom-10 text-center text-xs leading-relaxed text-niteroi-200">
        <p>{TEXTOS.orgao}</p>
        <p>Versão {versaoDoSistema()}</p>
      </div>
    </aside>
  )
}
